/**
 * Every page this app routes to, written once. A page is reached from the
 * navigation, from a row in a list, from a back button and from a redirect, so
 * a path spelled at each of those drifts one at a time; the routes in `App.tsx`
 * are the only other place these strings belong.
 *
 * A path taking route params is a function, so the segment order is written
 * down once rather than at each call site.
 */

/**
 * A path segment. Several callers build a link from a record still loading, so
 * the value can be absent — the path is then built exactly as interpolating it
 * did, rather than the link silently becoming the page above.
 */
type Segment = string | undefined;

/** Builds a query string from the params that have a value, in the order given. */
const withQuery = (href: string, params: Record<string, string | undefined>) => {
  const query = Object.entries(params)
    .flatMap(([key, value]) => (value ? [`${key}=${encodeURIComponent(value)}`] : []))
    .join('&');
  return query ? `${href}?${query}` : href;
};

/** The params a page addresses its own content by, written here and read there. */
export const TAB_PARAM = 'tab';
export const SUBJECT_PARAM = 'subject';
export const LESSON_PARAM = 'lesson';
export const CATEGORY_PARAM = 'category';
export const TOPIC_PARAM = 'topic';
export const NOTE_PARAM = 'note';
export const TAXONOMY_NODE_PARAM = 'node';
export const NILE_QUESTION_PART_PARAM = 'part';
export const NILE_QUESTION_STIMULUS_PARAM = 'stimulus';

const withTab = (href: string, tab?: string) => withQuery(href, { [TAB_PARAM]: tab });

/* Top level */

export const DASHBOARD_HREF = '/';
export const SIGN_IN_HREF = '/signin';
export const SIGN_UP_HREF = '/signup';
export const APPS_HREF = '/apps';
export const NILE_QUESTIONS_HREF = '/nile/questions';
export const NILE_NEW_QUESTION_HREF = `${NILE_QUESTIONS_HREF}/new`;
export const NILE_SOURCES_HREF = '/nile/sources';
export const NILE_TAXONOMY_HREF = '/nile/taxonomy';
export const NOTES_HREF = '/notes';
export const ROLL_HREF = '/roll';
export const TRACKER_HREF = '/tracker';
export const TUTORIALS_HREF = '/tutorials';
export const VIDEOS_HREF = '/videos';
export const STUDENT_ACTIVITY_LOG_HREF = '/student-activity-log';

export const SIGN_IN_REDIRECT_PARAM = 'redirect';
export const SIGN_UP_CONFIRM_PARAM = 'confirm';

/** Where a reader was headed, for the page that turned them away to send them back. */
export const withRedirect = (href: string, pathname?: string) =>
  withQuery(href, { [SIGN_IN_REDIRECT_PARAM]: pathname });

export const signInHref = (redirect?: string) => withRedirect(SIGN_IN_HREF, redirect);

/** `confirmed: false` resumes sign-up at the verification step. */
export const signUpHref = (params?: { confirmed: boolean }) =>
  withQuery(SIGN_UP_HREF, {
    [SIGN_UP_CONFIRM_PARAM]: params && String(params.confirmed),
  });

/* Content pages */

export const nileTaxonomyHref = (params?: { subject?: string; node?: string }) =>
  withQuery(NILE_TAXONOMY_HREF, {
    [SUBJECT_PARAM]: params?.subject,
    [TAXONOMY_NODE_PARAM]: params?.node,
  });

export const nileSourceHref = (sourceDocumentId: Segment) =>
  `${NILE_SOURCES_HREF}/${sourceDocumentId}`;

/** The question bank, opened on a subject's tab. */
export const nileQuestionsHref = (params?: { subject?: string }) =>
  withQuery(NILE_QUESTIONS_HREF, { [SUBJECT_PARAM]: params?.subject });

/** The form that writes a question by hand, filing it under the subject the bank was showing. */
export const nileNewQuestionHref = (params?: { subject?: string }) =>
  withQuery(NILE_NEW_QUESTION_HREF, { [SUBJECT_PARAM]: params?.subject });

/**
 * A question's editor, open at one of its parts, or at the root when none is
 * given; or at a stimulus, named with the part it is attached to.
 */
export const nileQuestionHref = (rootQuestionId: Segment, partId?: string, stimulusId?: string) =>
  withQuery(`${NILE_QUESTIONS_HREF}/${rootQuestionId}`, {
    [NILE_QUESTION_PART_PARAM]: partId,
    [NILE_QUESTION_STIMULUS_PARAM]: stimulusId,
  });

/** A student's own video, reached from the videos page and the player itself. */
export const videoHref = (subject: Segment, videoId: Segment) => `/video/${subject}/${videoId}`;

/** Why the player sent a reader back to the list, which reports it as a snackbar. */
export const VIDEOS_ERROR_PARAM = 'err';
export const VIDEOS_ERROR_NO_VIDEO = 'no-video';
export const VIDEOS_ERROR_NO_PERMISSION = 'no-permission';

/** The notes and videos lists address their content by query rather than by path. */
export const videosHref = (params?: {
  subject?: string;
  category?: string;
  topic?: string;
  error?: string;
}) =>
  withQuery(VIDEOS_HREF, {
    [SUBJECT_PARAM]: params?.subject,
    [CATEGORY_PARAM]: params?.category,
    [TOPIC_PARAM]: params?.topic,
    [VIDEOS_ERROR_PARAM]: params?.error,
  });

export const notesHref = (params?: { subject?: string; category?: string; note?: string }) =>
  withQuery(NOTES_HREF, {
    [SUBJECT_PARAM]: params?.subject,
    [CATEGORY_PARAM]: params?.category,
    [NOTE_PARAM]: params?.note,
  });

/* Analytics */

export const ANALYTICS_HREF = '/analytics';

/** The analytics page's quizzes tab, which a quiz attempt returns to. */
export const ANALYTICS_TAB_QUIZZES = 'quizzes';

export const studentAnalyticsHref = (
  studentId: Segment,
  params?: { tab?: string; subject?: string }
) =>
  withQuery(`${ANALYTICS_HREF}/${studentId}`, {
    [TAB_PARAM]: params?.tab,
    [SUBJECT_PARAM]: params?.subject,
  });

export const analyticsLegacyExamHref = (
  subject: Segment,
  examId: Segment,
  studentId: Segment,
  attemptNumber: number | string
) => `${ANALYTICS_HREF}/${subject}/${examId}/${studentId}/${attemptNumber}`;

/** Carries the subject its list was showing, so the way back lands on that subject. */
export const analyticsQuizAttemptHref = (
  studentId: Segment,
  attemptId: Segment,
  params?: { subject?: string }
) =>
  withQuery(`${ANALYTICS_HREF}/${studentId}/quizzes/${attemptId}`, {
    [SUBJECT_PARAM]: params?.subject,
  });

/* Quizzes */

export const QUIZ_HREF = '/quizzes';
export const QUIZ_NEW_HREF = `${QUIZ_HREF}/new`;
/** A paper quiz's reading and writing timer, run from a lesson with no preset quiz. */
export const QUIZ_TIMER_HREF = `${QUIZ_HREF}/timer`;

/** The subject page the question sets list was opened from, which its breadcrumbs lead back to. */
export const FROM_SUBJECT_PARAM = 'from';

/** The question sets list of a question bank subject. */
export const quizHref = (params?: { subject?: string; from?: string }) =>
  withQuery(QUIZ_HREF, { [SUBJECT_PARAM]: params?.subject, [FROM_SUBJECT_PARAM]: params?.from });

/**
 * The compose form, opened on the subject the list was showing, from the
 * subject page the list leads back to.
 */
export const quizNewHref = (params?: { subject?: string; from?: string; lesson?: string }) =>
  withQuery(QUIZ_NEW_HREF, {
    [SUBJECT_PARAM]: params?.subject,
    [FROM_SUBJECT_PARAM]: params?.from,
    [LESSON_PARAM]: params?.lesson,
  });

export const quizSetHref = (setId: Segment, params?: { lesson?: string }) =>
  withQuery(`/quiz/${setId}`, { [LESSON_PARAM]: params?.lesson });

export const quizLiveHref = (setId: Segment, params?: { lesson?: string }) =>
  withQuery(`${quizSetHref(setId)}/live`, { [LESSON_PARAM]: params?.lesson });

/* Admin */

export const ADMIN_CLASSES_HREF = '/admin/classes';
export const ADMIN_FOLLOW_UP_HREF = '/admin/follow-up';
export const ADMIN_INVOICES_HREF = '/admin/invoices';
export const ADMIN_LEGACY_EXAM_ASSIGNMENT_HREF = '/admin/legacy-exam-assignment';
export const ADMIN_ONBOARDING_PREVIEW_HREF = '/admin/onboarding-preview';
export const ADMIN_PAYROLL_HREF = '/admin/payroll';
export const ADMIN_PEOPLE_HREF = '/admin/people';
export const ADMIN_PRINTING_HREF = '/admin/printing';
export const ADMIN_QUIZ_ASSIGNMENT_HREF = '/admin/quiz-assignment';
export const ADMIN_ROLL_SUMMARY_HREF = '/admin/roll-summary';
export const ADMIN_SUBJECTS_HREF = '/admin/subjects';
export const ADMIN_TIMESHEET_HREF = '/admin/timesheet';

/** The people page's own tabs, which its profile pages return to. */
export const PEOPLE_TAB_STUDENTS = 'students';
export const PEOPLE_TAB_STAFF = 'staff';
export const PEOPLE_TAB_ENROLMENTS = 'enrolments';
export const PEOPLE_TAB_ENQUIRIES = 'enquiries';

export const peopleHref = (tab?: string) => withTab(ADMIN_PEOPLE_HREF, tab);

/** A student's own tabs, which the pages linking into one of them name. */
export const STUDENT_TAB_PROFILE = 'profile';
export const STUDENT_TAB_CLASSES = 'classes';
export const STUDENT_TAB_BILLING = 'billing';
export const STUDENT_TAB_SETTINGS = 'settings';

export const adminStudentHref = (studentId: Segment, tab?: string) =>
  withTab(`/admin/student/${studentId}`, tab);

export const adminStaffHref = (staffId: Segment) => `/admin/staff/${staffId}`;

/** A lead is an enrolment or an enquiry, and its type is part of its path. */
export const adminLeadHref = (leadType: Segment, leadId: Segment) =>
  `/admin/lead/${leadType}/${leadId}`;

export const adminClassHref = (subject: Segment, classId: Segment) =>
  `/admin/class/${subject}/${classId}`;

export const adminSubjectHref = (subject: Segment) => `${ADMIN_SUBJECTS_HREF}/${subject}`;

export const adminCourseHref = (subject: Segment, courseId: Segment, tab?: string) =>
  withTab(`/admin/course/${subject}/${courseId}`, tab);

export const adminStreamHref = (subject: Segment, courseId: Segment, streamId: Segment) =>
  `/admin/course/${subject}/${courseId}/${streamId}`;

export const adminLegacyExamsHref = (subject: Segment) => `/admin/legacy-exams/${subject}`;

export const adminLegacyExamHref = (subject: Segment, examId: Segment) =>
  `/admin/legacy-exams/${subject}/${examId}`;

export const adminLegacyTopicsHref = (subject: Segment, groupId: Segment) =>
  `/admin/legacy-topics/${subject}/${groupId}`;

export const adminNotesHref = (subject: Segment) => `/admin/notes/${subject}`;

export const adminNotesCategoryHref = (subject: Segment, category: Segment) =>
  `/admin/notes/${subject}/${category}`;

export const adminVideosHref = (subject: Segment) => `/admin/videos/${subject}`;

export const adminVideoCategoryHref = (subject: Segment, category: Segment) =>
  `/admin/videos/${subject}/${category}`;

export const adminVideoTopicHref = (subject: Segment, category: Segment, topic: Segment) =>
  `/admin/videos/${subject}/${category}/${topic}`;

export const adminVideoHref = (subject: Segment, videoId: Segment) =>
  `/admin/video/${subject}/${videoId}`;

/* Teacher */

export const TEACHER_CLASSES_HREF = '/teacher/classes';
export const TEACHER_LEGACY_EXAMS_HREF = '/teacher/legacy-exams';
export const TEACHER_LEGACY_MARKING_HREF = '/teacher/legacy-marking';

export const teacherClassHref = (subject: Segment, classId: Segment) =>
  `/teacher/class/${subject}/${classId}`;

export const teacherLegacyExamsHref = (subject?: string) =>
  withQuery(TEACHER_LEGACY_EXAMS_HREF, { [SUBJECT_PARAM]: subject });

export const teacherLegacyExamHref = (subject: Segment, examId: Segment) =>
  `${TEACHER_LEGACY_EXAMS_HREF}/${subject}/${examId}`;

export const legacyMarkingHref = (
  subject: Segment,
  examId: Segment,
  studentId: Segment,
  attemptNumber: number | string
) => `${TEACHER_LEGACY_MARKING_HREF}/${subject}/${examId}/${studentId}/${attemptNumber}`;

/* Rescheduling */

/** A lesson id carries characters a path separates on, so it is encoded here. */
export const rollRescheduleHref = (
  subject: Segment,
  classId: Segment,
  lesson: string,
  studentId: Segment
) => `${ROLL_HREF}/${subject}/${classId}/${encodeURIComponent(lesson)}/reschedule/${studentId}`;

export const studentRescheduleHref = (subject: Segment, classId: Segment, lesson: string) =>
  `/student/${subject}/${classId}/${encodeURIComponent(lesson)}/reschedule`;

/* Settings */

export const SETTINGS_HREF = '/settings';
export const SETTINGS_ACCOUNT_HREF = `${SETTINGS_HREF}/account`;
export const SETTINGS_SCHOOLS_HREF = `${SETTINGS_HREF}/schools`;
export const GROUPS_HREF = `${SETTINGS_HREF}/access/groups`;
export const PERMISSIONS_HREF = `${SETTINGS_HREF}/access/permissions`;
export const PRICES_HREF = `${SETTINGS_HREF}/prices`;

export const groupHref = (groupId: Segment) => `${GROUPS_HREF}/${groupId}`;
export const permissionHref = (tag: Segment) => `${PERMISSIONS_HREF}/${tag}`;
export const priceHref = (priceId: Segment) => `${PRICES_HREF}/${priceId}`;
